import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';

export interface VhChecklistOption {
  value: string;
  label: string;
  count?: number;
}

/**
 * Multi-select checklist used in filter sidebars. Pure input/output —
 * parent owns the selected set so URL sync is trivial.
 */
@Component({
  selector: 'vh-filter-checklist',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <ul class="vh-checklist" role="group" [attr.aria-label]="ariaLabel()">
      @for (opt of visibleOptions(); track opt.value) {
        <li class="vh-checklist__item">
          <label>
            <input
              type="checkbox"
              [value]="opt.value"
              [checked]="isSelected(opt.value)"
              (change)="toggle(opt.value, $event)"
            />
            <span class="vh-checklist__label">{{ opt.label }}</span>
            @if (opt.count !== null && opt.count !== undefined) {
              <span class="vh-checklist__count">{{ opt.count }}</span>
            }
          </label>
        </li>
      }
    </ul>
    @if (options().length > maxVisible() && !expanded()) {
      <button type="button" class="vh-checklist__more" (click)="expand()">
        Show {{ options().length - maxVisible() }} more
      </button>
    }
  `,
  styleUrl: './filter-checklist.component.scss',
})
export class VhFilterChecklistComponent {
  readonly options = input.required<ReadonlyArray<VhChecklistOption>>();
  readonly selected = input<ReadonlySet<string> | ReadonlyArray<string>>([]);
  readonly ariaLabel = input<string>('Filter options');
  readonly maxVisible = input<number>(8);

  readonly selectedChange = output<ReadonlyArray<string>>();

  protected readonly expanded = signal(false);

  readonly selectedSet = computed(() => {
    const s = this.selected();
    return s instanceof Set ? s : new Set(s);
  });

  readonly visibleOptions = computed(() => {
    const opts = this.options();
    if (this.expanded()) return opts;
    return opts.slice(0, this.maxVisible());
  });

  isSelected(value: string): boolean {
    return this.selectedSet().has(value);
  }

  toggle(value: string, event: Event): void {
    const checked = (event.target as HTMLInputElement).checked;
    const next = new Set(this.selectedSet());
    if (checked) next.add(value);
    else next.delete(value);
    this.selectedChange.emit(Array.from(next));
  }

  expand(): void {
    this.expanded.set(true);
  }
}
